import { useEffect, useState } from 'react';
import { Plus, FolderPlus, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * Bulk "Add to catalog" — same UX as the single-item picker but takes N SKUs
 * and hits POST /catalogs/add-items. Rendered inside the Catalog selection
 * bar when the user is in multi-select mode.
 */
export default function BulkAddToCatalogDialog({ open, onOpenChange, skus, onAdded }) {
  const [catalogs, setCatalogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/catalogs').then(({ data }) => setCatalogs(data.items || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const addTo = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post('/catalogs/add-items', { skus, ...payload });
      toast.success(data.created ? `Catalog created with ${data.added} items` : `${data.added} added — ${data.item_count} total`);
      onAdded?.(data);
      onOpenChange(false);
      setNewName('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bulk-add-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="h-display text-2xl">Add {skus.length} products to catalog</DialogTitle>
          <p className="sans text-xs uppercase tracking-widest text-neutral-500">Existing items in the catalog are skipped.</p>
        </DialogHeader>

        <div className="mt-3">
          <p className="eyebrow mb-2">Existing catalogs</p>
          {loading ? (
            <p className="sans text-sm text-neutral-500">Loading…</p>
          ) : catalogs.length === 0 ? (
            <p className="sans text-sm text-neutral-500">No catalogs yet — create one below.</p>
          ) : (
            <ul className="border border-neutral-200 divide-y divide-neutral-100 max-h-56 overflow-y-auto">
              {catalogs.map((c) => (
                <li key={c.id}>
                  <button
                    type="button" disabled={busy}
                    onClick={() => addTo({ catalog_id: c.id })}
                    className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center justify-between"
                    data-testid={`bulk-add-to-${c.id}`}
                  >
                    <span className="sans text-sm text-neutral-900">{c.name}</span>
                    <span className="sans text-xs text-neutral-500">{(c.item_skus || []).length} items</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-5">
          <p className="eyebrow mb-2">Or create new</p>
          <div className="flex gap-2">
            <input
              value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newName.trim() && addTo({ new_catalog_name: newName.trim() })}
              placeholder="Catalog name"
              className="ije-input h-10 flex-1"
              data-testid="bulk-add-new-name"
            />
            <button type="button" onClick={() => addTo({ new_catalog_name: newName.trim() })}
              disabled={busy || !newName.trim()} className="btn-primary h-10" data-testid="bulk-add-create">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            </button>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline h-10">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
